// Protection Analytics Report — shared types.
//
// The report is a user-arrangeable dashboard: metric slots, chart slots and a
// dimensional drill-down grid, each independently swappable in Edit mode and
// persisted to localStorage. These types are written so the mock layer could
// later be swapped for real :9000 endpoints without touching the UI.

/** Every record the stores ingest carries a created_at (the Zustand stores break without it). */
export interface Timestamped {
	created_at: string
}

/* ------------------------------------------------------------------ metrics */

export type MetricKey =
	| 'total_clicks'
	| 'ctr'
	| 'bounce_rate'
	| 'avg_clicks_per_visitor'
	| 'avg_page_views'
	| 'user_interaction_level'
	| 'avg_clicks_to_convert'
	| 'avg_time_on_site'
	| 'engaged_clicks'
	| 'impressions'

/** How a metric's raw value is rendered. */
export type MetricFormat = 'count' | 'percent' | 'duration' | 'decimal' | 'level'

export interface MetricDef extends Timestamped {
	key: MetricKey
	label: string
	/** Raw value; formatted per `format` at render time. */
	value: number
	format: MetricFormat
	/** Optional trend chip. */
	change?: { trend: 'positive' | 'negative'; value: string }
	/** Short help text surfaced via the card's help tooltip. */
	help?: string
}

/* ------------------------------------------------------------------- charts */

export type ChartKey =
	| 'devices'
	| 'ip_usage'
	| 'campaign_types'
	| 'repeated_clicks'
	| 'blacklist_entries'
	| 'traffic_timeline'
	| 'engagement_funnel'
	| 'roi_protection'

export type ChartType = 'pie' | 'bar' | 'area' | 'funnel'

export interface ChartDef extends Timestamped {
	key: ChartKey
	label: string
	type: ChartType
	/** Generic row data; shape depends on `type` (see mock-data). */
	data: Array<Record<string, string | number>>
	/** Keys in each datum that carry series values. */
	series: Array<{ key: string; label: string; color: string }>
	/** Category key (x-axis / slice name). */
	categoryKey: string
}

/* --------------------------------------------------------------- dimensions */

/** A leaf dimension the grid can group rows by. */
export type DimensionId = string

/** A dimension as the picker knows it. Values are never enumerated client-side —
 *  they come back with the rows, and a row's icon hint travels as `GridRow.iconMeta`. */
export interface DimensionDef {
	id: DimensionId
	label: string
}

/** Left-pane category for the TwoPaneMenu primary picker. */
export interface DimensionCategory {
	id: string
	label: string
	dimensionIds: DimensionId[]
}

/* -------------------------------------------------------------- grid model */

/** Valid / suspicious / invalid (bad) breakdown used by the veracity gauge. */
export interface Veracity {
	valid: number
	suspicious: number
	invalid: number
}

export interface GridRow extends Timestamped {
	id: string
	/** Dimension value label, e.g. "Google" / "Mobile". */
	label: string
	/** Which dimension this row belongs to (for icon resolution). */
	dimensionId: DimensionId
	/** Raw value id (used to resolve a source/brand icon). */
	valueId: string
	/**
	 * Leading-icon hint from the live engine: the ad-platform code for channel
	 * dimensions (→ brand logo) or the country ISO for region/city (→ flag). Null
	 * when the value itself resolves the icon (country, ad_platform) or on mock rows.
	 */
	iconMeta?: string | null
	/** Total clicks (the anchor metric). */
	clicks: number
	/** valid / suspicious / invalid(=bad) split of `clicks`. */
	veracity: Veracity
	/** Distinct sources blocked for this segment. */
	blockedSources: number
	/**
	 * Prevented ad waste for this row from the live engine — null renders an
	 * en-dash (unavailable at geo level, §5.5). Absent on mock rows, which fall
	 * back to the derived preventedWaste().
	 */
	waste?: number | null
	/** Whether this row can be drilled into (live: has children for the next dim). */
	hasChildren?: boolean
	/** Lazily-generated drill-down children, grouped by the second dimension. */
	children?: GridRow[]
}

/* ------------------------------------------------ deepdive table columns */

/**
 * Selectable metric columns for the deepdive table (Gustavo's spec). `total_clicks`
 * is the always-on anchor; `veracity` (gauge) is optional/off by default;
 * `deep_dive` is the per-row link. `bad` == invalid traffic, `suspicious` ==
 * suspicious traffic (Status: Bad / Suspicious).
 */
export type ColumnKey =
	| 'total_clicks'
	| 'suspicious_clicks'
	| 'bad_clicks'
	| 'bad_traffic_rate'
	| 'blocked_sources'
	| 'veracity'
	| 'deep_dive'

export interface ColumnDef {
	key: ColumnKey
	label: string
	sortable: boolean
	/** Can the user toggle it in the column selector? (false = always on). */
	toggleable: boolean
	/** Shown by default. */
	defaultOn: boolean
}

/* -------------------------------------- deepdive table settings (persisted) */

/**
 * The live deepdive table's metric column keys. Source of truth for the labels /
 * renderers is `METRIC_COLS` in dimension-grid; this key union is shared so the
 * layout store can persist column choice + order in a view's config. The
 * `Dimension` column is always pinned first and is not a metric column.
 */
export type MetricColKey =
	| 'analyzed'
	| 'valid'
	| 'suspicious'
	| 'bad'
	| 'rate'
	| 'blocked'
	| 'waste'

/** Render order of the metric columns = METRIC_COLS array order. */
export const ALL_METRIC_COL_KEYS: MetricColKey[] = [
	'analyzed',
	'valid',
	'suspicious',
	'bad',
	'rate',
	'blocked',
	'waste'
]

/** Columns shown by default (the rest ship hidden, toggled on via the menu). */
export const DEFAULT_VISIBLE_COL_KEYS: MetricColKey[] = ['analyzed', 'rate', 'blocked', 'waste']

/**
 * The deepdive table's saveable settings — the chosen dimensions plus which
 * metric columns show and in what order. Part of a view's `ReportLayout` so a
 * user's table arrangement is retained across reload and view switches (sort,
 * search, filter and pagination stay transient, like the charts' tooltips).
 */
export interface TableConfig {
	/** Primary (required) dimension. */
	dim1: DimensionId
	/** First drill dimension, or null for a flat Dim-1 list. */
	dim2: DimensionId | null
	/** Second drill dimension, or null. */
	dim3: DimensionId | null
	/** Which metric columns are visible. */
	visibleColumns: MetricColKey[]
	/** Left→right display order of the metric columns (Dimension stays pinned). */
	columnOrder: MetricColKey[]
}
